import {
  HdBodygraphCenter,
  HdBodygraphCircuitDefinition,
  HdBodygraphPlanetaryActivation,
} from '../hd-bodygraph.models';
import { HD_BODYGRAPH_CIRCUIT_THEME_ICONS } from '../hd-bodygraph-circuit-metadata';
import { getHdBodygraphChannel } from '../hd-bodygraph-metadata';
import { getHdGateIchingChar } from '../hd-gate-metadata';
import { HD_PLANET_DISPLAY_CONFIGS_WITH_CHIRON } from '../planet-grid/hd-planet-display-config';
import { HdChannelTranslation } from '../hd-gate-info.service';
import { HdChannelInfoRequestedContext } from './hd-channel-info-requested-context.model';
import {
  HD_EMOTIONAL_WAVE_SOURCE_GATE,
  HdEmotionalWaveRef,
  hdEmotionalWaveForChannel,
} from '../../emotional-waves/hd-emotional-waves.metadata';

const P = 'humanDesign.chart.channelInfo';

/**
 * Smalle dependency-facade zodat de VM-builder puur en zonder TestBed
 * testbaar is. De component vult dit vanuit HdGateInfoService,
 * HdPlanetInfoService en Transloco (content-taal).
 */
export interface HdChannelInfoVmDeps {
  channelTranslation(channelKey: string): HdChannelTranslation | undefined;
  circuitFor(channelKey: string): HdBodygraphCircuitDefinition | undefined;
  circuitName(circuitKey: string): string;
  centerLabel(center: HdBodygraphCenter): string;
  gateKeynotes(gate: number): { keynote1: string; keynote2: string };
  lineKeynote(gate: number, line: number): string;
  planetName(planetId: number): string;
  planetSubtitle(planetId: number): string;
  planetGlyph(planetId: number): string;
  t(key: string, params?: Record<string, unknown>): string;
}

export interface HdChannelInfoActivationRow {
  readonly key: string;
  readonly glyph: string;
  readonly sideLabel: string;
  readonly isDesign: boolean;
  readonly planetName: string;
  /** Keynote van de planeet (i18n `subtitle`) — getoond als hover-tooltip. */
  readonly planetSubtitle: string;
  /** Bv. "lijn 4 · Triomf" — lijnnummer + lijnkeynote. */
  readonly lineLabel: string;
  readonly gate: number;
  readonly ichingChar: string;
  /** Bv. "34 · Kracht van het Grote, Macht" — keynotes NOOIT afgekapt (chip wrapt). */
  readonly gateChipLabel: string;
  /**
   * Virtuele maan-transit-rij (spec 2026-07-06 §6.6): geen echte activatie
   * maar de tijdelijke maan-activatie van de maanpoort. Krijgt het
   * transit-blauw als accent en heeft bewust geen lijnlabel.
   */
  readonly isTransit?: boolean;
  readonly transitKind?: 'moon' | 'just-now' | 'cycle';
}

export interface HdChannelInfoSectionVm {
  readonly channelKey: string;
  readonly gatesLabel: string;
  /** De twee poorten van het kanaal, elk klikbaar naar de poort-detailinfo. */
  readonly gates: readonly number[];
  readonly nameLabel: string;
  readonly descriptionLabel: string;
  /** Circuitnaam + thema-keynote, gescheiden door ' - ' (keynote optioneel). */
  readonly circuitLabel: string | null;
  readonly circuitThemeIcon: string | null;
  readonly centersLabel: string;
  /** Emotionele golf van het kanaal (enkel de zeven Solar Plexus-kanalen), anders `null`. */
  readonly emotionalWave: HdChannelInfoEmotionalWaveVm | null;
  readonly rows: readonly HdChannelInfoActivationRow[];
}

export interface HdChannelInfoEmotionalWaveVm {
  readonly wave: HdEmotionalWaveRef;
  /** Scope-sleutel van de kanaal-keynote in `human-design/emotional-waves`. */
  readonly keynoteKey: string;
  /** Kanaal 59-6: de lijnen van de activaties van poort 6 (de bron licht die golven uit). */
  readonly sourceLines: readonly number[];
}

/** Volgorde-index per planetId, conform de planetengrid-volgorde. */
const PLANET_ORDER: ReadonlyMap<number, number> = new Map(
  HD_PLANET_DISPLAY_CONFIGS_WITH_CHIRON.map((config, index) => [config.planetId, index]),
);

/** PlanetId van de maan — bron voor de virtuele transit-rij (§6.6). */
const MOON_PLANET_ID = HD_PLANET_DISPLAY_CONFIGS_WITH_CHIRON.find(config => config.key === 'moon')!.planetId;

export function buildHdChannelInfoSections(
  context: HdChannelInfoRequestedContext,
  deps: HdChannelInfoVmDeps,
): HdChannelInfoSectionVm[] {
  return context.channels.map(entry => {
    const translation = deps.channelTranslation(entry.channelKey);
    const name = translation?.name ?? entry.channelKey;
    const shortDescription = translation?.shortDescription ?? '';
    const circuit = deps.circuitFor(entry.channelKey);
    const circuitKeynote = circuit?.theme
      ? deps.t(`humanDesign.properties.circuitryKeynotes.${circuit.theme}`)
      : '';
    const centers = getHdBodygraphChannel(entry.channelKey)?.centers ?? null;

    const rows = context.activations
      .filter(activation => entry.gates.includes(activation.gate))
      .sort((a, b) => {
        if (a.side !== b.side) {
          return a.side === 'personality' ? -1 : 1;
        }
        return (PLANET_ORDER.get(a.planetId) ?? 99) - (PLANET_ORDER.get(b.planetId) ?? 99);
      })
      .map(activation => buildRow(
        activation.gate, activation.side === 'design', activation.planetId, activation.line,
        deps, context.baseRowLabel ?? null,
      ));

    // Maan-transit modus: de maanpoort heeft geen echte activatie — voeg de
    // virtuele transit-rij toe ná de echte activaties (spec 2026-07-06 §6.6).
    if (context.moonTransitGate != null && entry.gates.includes(context.moonTransitGate)) {
      rows.push(buildMoonTransitRow(context.moonTransitGate, deps));
    }

    // Cycle charts hergebruiken de just-now-transitrij met een eigen label
    // ("Saturnus-return-transit") en kind (spec-verbetering ronde 1).
    const transitRowLabel = context.transitRowLabel
      ?? deps.t('humanDesign.chart.justNowTransit.transitRowLabel');
    const transitKind: HdChannelInfoActivationRow['transitKind'] =
      context.transitStyle === 'cycle' ? 'cycle' : 'just-now';
    const justNowRows = (context.justNowTransitActivations ?? [])
      .filter(activation => entry.gates.includes(activation.gate))
      .sort((left, right) => (PLANET_ORDER.get(left.planetId) ?? 99) - (PLANET_ORDER.get(right.planetId) ?? 99))
      .map(activation => buildJustNowTransitRow(activation, deps, transitRowLabel, transitKind));
    rows.push(...justNowRows);

    return {
      channelKey: entry.channelKey,
      gatesLabel: `${entry.gates[0]} – ${entry.gates[1]}`,
      gates: entry.gates,
      nameLabel: deps.t('humanDesign.properties.channelNameLabel', { name }),
      descriptionLabel: deps.t('humanDesign.properties.channelDescriptionLabel', { shortDescription }),
      circuitLabel: circuit
        ? (circuitKeynote
          ? `${deps.circuitName(circuit.key)} - ${circuitKeynote}`
          : deps.circuitName(circuit.key))
        : null,
      circuitThemeIcon: circuit?.theme ? HD_BODYGRAPH_CIRCUIT_THEME_ICONS[circuit.theme] : null,
      centersLabel: centers ? `${deps.centerLabel(centers[0])} ↔ ${deps.centerLabel(centers[1])}` : '',
      emotionalWave: buildEmotionalWave(entry.channelKey, context),
      rows,
    };
  });
}

function buildEmotionalWave(
  channelKey: string,
  context: HdChannelInfoRequestedContext,
): HdChannelInfoEmotionalWaveVm | null {
  const match = hdEmotionalWaveForChannel(channelKey);
  if (!match) return null;
  return {
    wave: match.wave,
    keynoteKey: `channels.${match.channelKey}.keynote`,
    sourceLines: match.wave === 'source'
      ? context.activations
        .filter(activation => activation.gate === HD_EMOTIONAL_WAVE_SOURCE_GATE)
        .map(activation => activation.line)
      : [],
  };
}

/**
 * Virtuele maan-transit-rij: maan-glyph + "Maan-transit"-label, zonder
 * lijnlabel (we tonen voor de maan bewust geen lijn-niveau, spec §2).
 */
function buildMoonTransitRow(gate: number, deps: HdChannelInfoVmDeps): HdChannelInfoActivationRow {
  const keynotes = deps.gateKeynotes(gate);
  const keynoteText = [keynotes.keynote1, keynotes.keynote2].filter(Boolean).join(', ');
  return {
    key: `moon-transit-${gate}`,
    glyph: deps.planetGlyph(MOON_PLANET_ID),
    sideLabel: deps.t('humanDesign.chart.moonTransit.transitRowLabel'),
    isDesign: false,
    planetName: deps.planetName(MOON_PLANET_ID),
    planetSubtitle: deps.planetSubtitle(MOON_PLANET_ID),
    lineLabel: '',
    gate,
    ichingChar: getHdGateIchingChar(gate),
    gateChipLabel: keynoteText ? `${gate} · ${keynoteText}` : `${gate}`,
    isTransit: true,
    transitKind: 'moon',
  };
}

function buildJustNowTransitRow(
  activation: HdBodygraphPlanetaryActivation,
  deps: HdChannelInfoVmDeps,
  sideLabel: string,
  transitKind: HdChannelInfoActivationRow['transitKind'],
): HdChannelInfoActivationRow {
  const keynotes = deps.gateKeynotes(activation.gate);
  const keynoteText = [keynotes.keynote1, keynotes.keynote2].filter(Boolean).join(', ');
  const lineKeynote = deps.lineKeynote(activation.gate, activation.line);
  const lineWord = deps.t(`${P}.line`);
  return {
    key: `just-now-${activation.id}`,
    glyph: deps.planetGlyph(activation.planetId),
    sideLabel,
    isDesign: false,
    planetName: deps.planetName(activation.planetId),
    planetSubtitle: deps.planetSubtitle(activation.planetId),
    lineLabel: lineKeynote
      ? `${lineWord} ${activation.line} · ${lineKeynote}`
      : `${lineWord} ${activation.line}`,
    gate: activation.gate,
    ichingChar: getHdGateIchingChar(activation.gate),
    gateChipLabel: keynoteText ? `${activation.gate} · ${keynoteText}` : `${activation.gate}`,
    isTransit: true,
    transitKind,
  };
}

function buildRow(
  gate: number,
  isDesign: boolean,
  planetId: number,
  line: number,
  deps: HdChannelInfoVmDeps,
  baseRowLabel: string | null,
): HdChannelInfoActivationRow {
  const keynotes = deps.gateKeynotes(gate);
  const keynoteText = [keynotes.keynote1, keynotes.keynote2].filter(Boolean).join(', ');
  const lineKeynote = deps.lineKeynote(gate, line);
  const lineWord = deps.t(`${P}.line`);
  const sideLabel = deps.t(isDesign ? `${P}.sideDesign` : `${P}.sidePersonality`);
  return {
    key: `${isDesign ? 'design' : 'personality'}-${planetId}-${gate}-${line}`,
    glyph: deps.planetGlyph(planetId),
    // Composite: chart 1-naam vóór het kant-label, zoals de partner-rijen hun naam dragen.
    sideLabel: baseRowLabel ? `${baseRowLabel} · ${sideLabel}` : sideLabel,
    isDesign,
    planetName: deps.planetName(planetId),
    planetSubtitle: deps.planetSubtitle(planetId),
    lineLabel: lineKeynote ? `${lineWord} ${line} · ${lineKeynote}` : `${lineWord} ${line}`,
    gate,
    ichingChar: getHdGateIchingChar(gate),
    gateChipLabel: keynoteText ? `${gate} · ${keynoteText}` : `${gate}`,
  };
}
